/**
 * Whether the server is actually reachable.
 *
 * `navigator.onLine` only reports whether the device has a network interface
 * up. It stays true when the server is down, when a VPN drops, and on a
 * captive portal — so on its own it hides nothing and shows nothing at the
 * moments that matter.
 *
 * This tracks what requests actually did: a request that failed with no
 * response means unreachable, any response at all means reachable. Installed
 * as an axios interceptor so every call in the app reports in, rather than
 * each caller having to remember to.
 */

let reachable = true;
const listeners = new Set();

const notify = () => {
  for (const fn of listeners) {
    try {
      fn(reachable);
    } catch (e) {
      console.warn('connectivity: listener failed', e);
    }
  }
};

const set = (value) => {
  if (value === reachable) return;
  reachable = value;
  notify();
};

export const isReachable = () => reachable;
export const reportReachable = () => set(true);
export const reportUnreachable = () => set(false);

export const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Treat the app as connected only when the interface is up AND the server answered. */
export const isOnline = () => navigator.onLine !== false && reachable;

/**
 * Report every axios outcome. A rejection carrying a response means the server
 * answered — a 404 or a 500 is not being offline.
 */
export const installConnectivityProbe = (axios) =>
  axios.interceptors.response.use(
    (response) => {
      reportReachable();
      return response;
    },
    (error) => {
      if (error && error.response) reportReachable();
      else reportUnreachable();
      return Promise.reject(error);
    },
  );

// Test-only: reset module state between cases.
export const _resetConnectivity = () => {
  reachable = true;
  listeners.clear();
};
